import i18n from "../util/i18n.ts";

/**
 * A3 zero-value/empty-state demotion (MOBILE_CONTENT_FIRST_PLAN_2026-07-06.md).
 *
 * Leaderboard/leader modules must not render meaningless data ("0.0 leaders"
 * at the start of a season). This is additive on top of the P2S-5 empty-state
 * handling in Leaders.tsx: P2S-5 covers "no data generated / no filter
 * results", A3 covers "data exists but is not meaningful yet".
 */

export const MIN_MEANINGFUL_TEAM_GAMES = 5;

export type LeaderboardMeaningfulness =
  | { meaningful: true }
  | {
      meaningful: false;
      reason: "insufficient-games" | "zero-top-value";
    };

export const getLeaderboardMeaningfulness = ({
  topValue,
  userTeamGamesPlayed,
}: {
  /** The leading value of the board (spotlight/top metric), if any. */
  topValue: number | undefined;
  /**
   * User team games played this regular season. undefined means the scope is
   * historical/career/all-seasons, where the gate must never hide archives.
   */
  userTeamGamesPlayed: number | undefined;
}): LeaderboardMeaningfulness => {
  if (userTeamGamesPlayed === undefined) {
    return { meaningful: true };
  }

  if (userTeamGamesPlayed < MIN_MEANINGFUL_TEAM_GAMES) {
    return { meaningful: false, reason: "insufficient-games" };
  }

  if (topValue !== undefined && (!Number.isFinite(topValue) || topValue <= 0)) {
    return { meaningful: false, reason: "zero-top-value" };
  }

  return { meaningful: true };
};

/** Count-0 highlight/badge cards (e.g. "Team highlights 0") must not render. */
export const isCountHighlightRenderable = (count: number | undefined): boolean =>
  typeof count === "number" && Number.isFinite(count) && count > 0;

/** One-line replacement for a hidden leaderboard module. */
export const getStatAccumulatingMessage = (gamesPlayed: number): string =>
  i18n.t("statMeaningfulness.accumulating", {
    ns: "ui",
    count: gamesPlayed,
    defaultValue: "Season data is still accumulating ({{count}} games played)",
  });

/** Label for the previous-season review entry next to the accumulating line. */
export const getPreviousSeasonReviewLabel = (season: number): string =>
  i18n.t("statMeaningfulness.previousSeasonReview", {
    ns: "ui",
    season,
    defaultValue: "Review the {{season}} season",
  });
